import React from 'react'
import { Stack, Typography } from '@mui/material'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import { useTranslation } from 'react-i18next'

import { BEIGE_300, BEIGE_600, DARK_ORANGE, GRAY_700, GRAY_900 } from 'constants/styling/theme'
import { MUIButton } from 'components/common/MUIButton'

interface Props {
  selectionId: string
  name: string
  /** The pack's total, already formatted; null while a line is unpriced. */
  totalLabel: string | null
  onRemove: (selectionId: string) => void
  /** False once the order is placed: the cart can no longer change. */
  removable?: boolean
  children: React.ReactNode
}

/** The lines of one pack purchase, under the pack's name, removed only together. */
export const PackCartGroup: React.FC<Props> = ({ selectionId, name, totalLabel, onRemove, removable = true, children }) => {
  const { t } = useTranslation(['purchase_flow_revamp'])

  return (
    <Stack
      gap={0.8}
      data-cy="pf-revamp-cart-pack"
      sx={{ border: `1px solid ${BEIGE_600}`, borderRadius: '0.8rem', padding: 1.2, backgroundColor: BEIGE_300 }}
    >

      <Stack direction="row" alignItems="center" justifyContent="space-between" gap={0.8}>
        <Typography variant="text-sm" fontWeight={600} color={GRAY_900}>{name}</Typography>
        {totalLabel &&
          <Typography variant="text-sm" fontWeight={600} color={GRAY_900} sx={{ whiteSpace: 'nowrap' }}>{totalLabel}</Typography>
        }
      </Stack>

      {children}

      {removable &&
        <Stack direction="row" justifyContent="flex-end">
          <MUIButton
            type="secondaryNoBorder"
            onClick={() => onRemove(selectionId)}
            aria-label={t('purchase_flow_revamp:cart.remove_aria')}
            startIcon={<DeleteOutlineIcon sx={{ fontSize: '2rem' }} />}
            sx={{
              minWidth: 0,
              padding: '0.4rem 0.4rem',
              color: GRAY_700,
              textTransform: 'none',
              '& .MuiButton-startIcon': { marginRight: '0.4rem', marginLeft: 0 },
              backgroundColor: 'transparent',
              '&:hover': { backgroundColor: 'transparent', color: DARK_ORANGE },
            }}
            data-cy="pf-revamp-cart-pack-remove"
          >
            <Typography variant="text-sm" fontWeight={550} color="inherit">
              {t('purchase_flow_revamp:cart.remove')}
            </Typography>
          </MUIButton>
        </Stack>
      }

    </Stack>
  )
}
